import { DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useHasPermission } from '@/auth';
import useClientState from '@/state/useClientState';
import { envVariables } from '@/utils/env';
import {
  createPlankToMwdDragChannel,
  isFreshPlankToMwdDragPayload,
  isPlankToMwdDragMessage,
  isResolvablePlankToMwdDragPayload,
  PLANK_TO_MWD_DRAG_PAYLOAD_MAX_AGE_MS,
  PlankToMwdDragPayload,
  PlankToMwdDragSession,
  readPlankToMwdDragPayload,
} from '@/utils/plankToMwdDrag';
import { toastErrorOptions, toastInfoOptions } from '@/utils/toastOptions';

import useOpenWerklijnFromPlankDrop from './useOpenWerklijnFromPlankDrop';

/** Reads the payload from the DataTransfer on drop, falling back to the last broadcast payload.
 * The drag always comes from another window, where a DataTransfer is not guaranteed to survive.
 */
function readDropPayload(event: DragEvent<HTMLElement>, fallback: PlankToMwdDragPayload | null) {
  try {
    return readPlankToMwdDragPayload(event.dataTransfer) ?? fallback;
  } catch {
    // Missing transport data may use the broadcast; malformed/old-version data may not.
    return null;
  }
}

/** Owns the cross-window lifecycle of a Plank-to-MWD drag on the MWD side, keeping
 * MwdPlankDropOverlay purely presentational.
 */
export default function usePlankDropCoordinator() {
  const featurePlankMwdDragDrop = !!envVariables().featurePlankMwdDragDrop;
  const hasMwdView = useHasPermission('mwd.view');
  const hasPlatformView = useHasPermission('platform.view');
  const canAcceptPlankDrop = featurePlankMwdDragDrop && (hasMwdView || hasPlatformView);

  const openWerklijnFromPlankDrop = useOpenWerklijnFromPlankDrop();
  // A user-initiated MWD search finishes with `vervangWerklijnen`, which would throw away a
  // werklijn added by a drop that landed while it was still running.
  const isSearching = useClientState((state) => state.searchStatus.isSearching);
  const [activePayload, setActivePayload] = useState<PlankToMwdDragPayload | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  // A drop starts an async search; a second drop would race it. In a ref so the drag handlers
  // read the current value without being re-created.
  const isDropInProgress = useRef(false);
  const completedSessions = useRef(new Map<string, number>());
  const completeSession = useCallback((session: PlankToMwdDragSession) => {
    // A terminal message can arrive before START (different sending tabs/channels).
    // Retain its identity only while that START could still be fresh.
    const now = Date.now();
    for (const [id, expiresAt] of completedSessions.current) {
      if (expiresAt <= now) completedSessions.current.delete(id);
    }
    completedSessions.current.set(session.sessionId, session.timestamp + PLANK_TO_MWD_DRAG_PAYLOAD_MAX_AGE_MS);
    setActivePayload((current) => (current?.sessionId === session.sessionId ? null : current));
  }, []);
  const consumeSession = useCallback(
    (payload: PlankToMwdDragPayload) => {
      completeSession(payload);
      // Consumption ends the drag in every tab, including explicitly rejected drops.
      channelRef.current?.postMessage({ type: 'PLANK_MWD_DROP_CONSUMED', payload });
    },
    [completeSession],
  );

  useEffect(() => {
    if (!canAcceptPlankDrop) return undefined;

    const channel = createPlankToMwdDragChannel();
    if (!channel) return undefined;
    channelRef.current = channel;
    let latestStartTimestamp = -Infinity;

    // The overlay comes from the BroadcastChannel, never the DataTransfer: during dragover the
    // browser exposes only `types`, so the dragged items are unknowable until the drop.
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (!isPlankToMwdDragMessage(message)) return;

      if (message.type === 'PLANK_MWD_DRAG_START') {
        if (
          isFreshPlankToMwdDragPayload(message.payload) &&
          isResolvablePlankToMwdDragPayload(message.payload) &&
          !completedSessions.current.has(message.payload.sessionId) &&
          message.payload.timestamp >= latestStartTimestamp
        ) {
          latestStartTimestamp = message.payload.timestamp;
          setActivePayload(message.payload);
        }
        return;
      }

      const session = message.type === 'PLANK_MWD_DRAG_END' ? message : message.payload;
      if (isFreshPlankToMwdDragPayload(session)) completeSession(session);
    };

    return () => {
      channelRef.current = null;
      channel.close();
      completedSessions.current.clear();
      setActivePayload(null);
    };
  }, [canAcceptPlankDrop, completeSession]);

  // Safety net for a drag that never reports its end (e.g. the Plank window closed mid-drag),
  // so the overlay cannot get stuck over MWD.
  useEffect(() => {
    if (!activePayload) return undefined;
    const timeout = globalThis.setTimeout(
      () => completeSession(activePayload),
      Math.max(0, activePayload.timestamp + PLANK_TO_MWD_DRAG_PAYLOAD_MAX_AGE_MS - Date.now()),
    );
    return () => globalThis.clearTimeout(timeout);
  }, [activePayload, completeSession]);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!canAcceptPlankDrop) return;
      // Accept native transport even while busy: onDrop rejects with feedback, without work.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [canAcceptPlankDrop],
  );

  const handleDrop = useCallback(
    async (event: DragEvent<HTMLElement>) => {
      if (!canAcceptPlankDrop) return;
      event.preventDefault();

      const payload = readDropPayload(event, activePayload);
      if (!payload || !isFreshPlankToMwdDragPayload(payload) || !isResolvablePlankToMwdDragPayload(payload)) {
        if (activePayload) consumeSession(activePayload);
        toast.error('Het gesleepte item kan niet aan MWD worden toegevoegd', toastErrorOptions);
        return;
      }

      if (completedSessions.current.has(payload.sessionId)) return;
      consumeSession(payload);

      if (isSearching) {
        toast.info('MWD is nog bezig met zoeken. Probeer het zo opnieuw.', toastInfoOptions);
        return;
      }

      if (isDropInProgress.current) {
        toast.info('Er wordt al een gesleept item toegevoegd. Probeer het zo opnieuw.', toastInfoOptions);
        return;
      }

      isDropInProgress.current = true;
      try {
        await openWerklijnFromPlankDrop(payload);
      } finally {
        isDropInProgress.current = false;
      }
    },
    [activePayload, canAcceptPlankDrop, consumeSession, isSearching, openWerklijnFromPlankDrop],
  );

  return {
    canAcceptPlankDrop,
    activePayload,
    handleDragOver,
    handleDrop,
  };
}
